import Header from "../../Common/header";
import Footer from "../../Common/footer";

import About1 from "../../images/aboutPage1.webp";
import About2 from "../../images/aboutPage2.webp";
import About3 from "../../images/aboutPage3.webp";

import useDocumentTitle from "../../Common/useDocumentTitle";

/**
 * Returns the About page component.
 *
 * @returns The About page component.
 */
const About = () => {
  useDocumentTitle("About Us");

  /**
   * SVG element consisting of 25 circles. Used for styling.
   * @param {*} style Tailwind CSS styles to be used
   * @returns svg element
   */
  function candyBox(style) {
    return (
      <svg className={style} viewBox="0 0 184 184" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="7.55556" cy="7.55556" r="7.55556" fill="#F4F4F4" />
        <circle cx="7.55556" cy="49.7777" r="7.55556" fill="#F4F4F4" />
        <circle cx="7.55556" cy="92" r="7.55556" fill="#F4F4F4" />
        <circle cx="7.55556" cy="134.222" r="7.55556" fill="#F4F4F4" />
        <circle cx="7.55556" cy="176.444" r="7.55556" fill="#F4F4F4" />
        <circle cx="49.7777" cy="7.55556" r="7.55556" fill="#F4F4F4" />
        <circle cx="49.7777" cy="49.7777" r="7.55556" fill="#F4F4F4" />
        <circle cx="49.7777" cy="92" r="7.55556" fill="#F4F4F4" />
        <circle cx="49.7777" cy="134.222" r="7.55556" fill="#F4F4F4" />
        <circle cx="49.7777" cy="176.444" r="7.55556" fill="#F4F4F4" />
        <circle cx="91.9999" cy="7.55556" r="7.55556" fill="#F4F4F4" />
        <circle cx="91.9999" cy="49.7777" r="7.55556" fill="#F4F4F4" />
        <circle cx="91.9999" cy="92" r="7.55556" fill="#F4F4F4" />
        <circle cx="91.9999" cy="134.222" r="7.55556" fill="#F4F4F4" />
        <circle cx="91.9999" cy="176.444" r="7.55556" fill="#F4F4F4" />
        <circle cx="134.222" cy="7.55556" r="7.55556" fill="#F4F4F4" />
        <circle cx="134.222" cy="49.7777" r="7.55556" fill="#F4F4F4" />
        <circle cx="134.222" cy="92" r="7.55556" fill="#F4F4F4" />
        <circle cx="134.222" cy="134.222" r="7.55556" fill="#F4F4F4" />
        <circle cx="134.222" cy="176.444" r="7.55556" fill="#F4F4F4" />
        <circle cx="176.444" cy="7.55556" r="7.55556" fill="#F4F4F4" />
        <circle cx="176.444" cy="49.7777" r="7.55556" fill="#F4F4F4" />
        <circle cx="176.444" cy="92" r="7.55556" fill="#F4F4F4" />
        <circle cx="176.444" cy="134.222" r="7.55556" fill="#F4F4F4" />
        <circle cx="176.444" cy="176.444" r="7.55556" fill="#F4F4F4" />
      </svg>
    );
  }

  /* style={{"objectPosition": "15%"}} */

  return (
    <>
      <Header />

      <main className="bg-white dark:bg-gray-900">
      <section className="bg-white relative isolate overflow-hidden dark:bg-gray-900">
        {/* Cool background blur effect */}
        <div
          className="absolute left-[calc(50%-4rem)] top-10 -z-10 transform-gpu blur-3xl sm:left-[calc(50%-18rem)] lg:left-48 lg:top-[calc(50%-30rem)] xl:left-[calc(50%-24rem)]"
          aria-hidden="true"
        >
          <div
            className=" aspect-[1108/632] w-[69.25rem] bg-gradient-to-r from-blue-800 to-blue-600 opacity-20" //from-[#80caff] to-[#4f46e5]
            style={{
              clipPath:
                'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 48.9% 0.2%, 73.6% 51.7%)',
            }}
          />
        </div>

        <div className="gap-16 items-center py-8 px-4 mx-auto max-w-screen-xl lg:grid lg:grid-cols-2 lg:py-10 lg:px-6">
          <div className="grid h-full grid-cols-1 gap-4">
            {candyBox("w-24 h-24 absolute -ml-10 mt-10 hidden lg:block")}
            {candyBox("w-24 h-24 absolute -mr-10 -mb-10 hidden lg:block justify-self-end self-end")}

            <img className="w-full h-full rounded-lg object-cover" src={About1} alt="Research Vessel" draggable="false"/>
          </div>

          <div className="text-gray-500">
            <h1 className="mb-8 text-7xl sm:text-9xl tracking-tight font-extrabold text-gray-900 dark:text-white">About Us</h1>

            <h2 className="text-4xl text-gray-900 font-bold dark:text-white">
              Arctic Research Foundation
            </h2>

            <p className="mt-6 text-gray-500 text-base sm:text-lg leading-8 dark:text-gray-400">
              The Arctic Research Foundation (ARF) is a private,
              non-profit organization creating a new scientific infrastructure for the Canadian Arctic through its efficient,
              cutting-edge research vessels and self-powered mobile labs.
              <br />
              <br />
              Over the years, especially in the current era of big data,
              the Arctic Research Foundation has generated and collected a large volume of big data.
              These include animal stock assessments, hydrographic and bathymetric data, information on soil and salinity samples,
              analysis of changing ice conditions, and data generated through the ARF Naurvik plant production pod.
              <br />
              <br />
              However, the disparate data from across the Arctic needs to be managed,
              catalogued, and made accessible through a publicly available database.
              The general objective of this project is to design and develop a universally readable
              and easily searchable database with a user-friendly and intuitive interface.
              This database will effectively manage and catalogue the data.
              <br />
              <br />
              This data and its metadata will be made available, as well as easily accessible and searchable by the public.
              This will further enhance ARF's innovative research delivery infrastructure by incorporating best-in-class
              research database and user-centric UX design elements to ensure efficient access to ARF’s
              expanding research assets for a diverse range of groups interested in accessing Arctic research.
            </p>
          </div>
        </div>
      </section>


      <section className="mt-32">
        <div className="bg-blue-600 relative lg:py-0 py-10">
          <div className="mx-auto lg:max-w-7xl lg:px-8 lg:grid lg:grid-cols-3 lg:gap-8">

            <div className="relative lg:-my-8">
              <div className="mx-auto px-4 sm:px-6 lg:px-0 h-full">
                <img
                  draggable="false"
                  src={About2}
                  alt="Arctic Research Snowmobiles"
                  className="rounded-xl object-cover h-full w-full"
                />
              </div>
            </div>

            <div className="mt-12 lg:mt-0 lg:col-span-2 lg:pl-8">
              <div className="mx-auto px-4 sm:px-6 lg:px-0 lg:py-20">
                <h2 className="mb-4 text-4xl tracking-tight font-bold text-white ">What We Do</h2>
                <p className="text-base sm:text-lg text-white leading-8">
                  We coordinate and catalyze scientific, cultural,
                  and economic research in the Arctic. We partner with governments,
                  universities, and other research institutions that require access
                  to innovative infrastructure to conduct program initiatives in the Arctic.
                  We build relationships with Arctic Indigenous people to advance the understanding
                  of the region with traditional knowledge.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>


      <section className="mt-32">
        <div className="bg-blue-600 relative lg:py-0 py-10">
          <div className="mx-auto lg:max-w-7xl lg:px-8 lg:grid lg:grid-cols-3 lg:gap-8">

            <div className="relative lg:hidden">
              <div className="mx-auto px-4 sm:px-6 lg:px-0 h-full">
                <img
                  draggable="false"
                  src={About3}
                  alt="Arctic Research Partners"
                  className="rounded-xl object-right object-cover lg:h-full lg:w-full"
                />
              </div>
            </div>

            <div className="mt-12 lg:mt-0 lg:col-span-2 lg:pl-8">
              <div className="mx-auto px-4 sm:px-6 lg:px-0 lg:py-20">
                <h2 className="mb-4 text-4xl text-white font-bold">Our Partners</h2>
                <p className="leading-8 text-base sm:text-lg text-white">
                  At Arctic Research Foundation, one of the greatest assets we have in pursuit
                  of our mandate is our ability to catalyze, mobilize and strategize with a
                  number of like-minded institutions and agencies both across Canada and abroad.
                  These partnerships are the backbone of our progress and we are constantly engaging
                  to expand with a mind to deepen our impact, work collaboratively and multiply our
                  outputs into the scientific community through shared resources and collective approaches to our objectives.
                </p>
              </div>
            </div>

            <div className="relative lg:-my-8 lg:block hidden">
              <div className="mx-auto px-4 sm:px-6 lg:p-0 h-full">
                <img
                  draggable="false"
                  src={About3}
                  alt="Arctic Research Partners"
                  className="rounded-xl object-right object-cover lg:h-full lg:w-full"
                />
              </div>
            </div>

          </div>
        </div>
      </section>
      </main>
      <div className="pb-32 bg-white dark:bg-gray-900"></div> <div className="pb-24 bg-white dark:bg-gray-900"></div> {/* Creates space between the last section and the footer. Not necessary in light mode but in dark mode you get a white space without this. */}
      <Footer />
    </>
  );
};

export default About;